import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Alert, Chip, Collapse, IconButton, Table, TableBody,
  TableCell, TableHead, TableRow, Paper, CircularProgress, Stack, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';

function downloadBlob(data, filename, mime) {
  const url = URL.createObjectURL(new Blob([data], { type: mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function GabbaiOrdersSummary() {
  const inNextDesign = useCkInside();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [exporting, setExporting] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/gabbai-orders/summary');
      setData(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בטעינת הזמנות גבאים');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleExportOrders = async () => {
    setExporting('orders');
    setError('');
    try {
      const response = await api.get('/gabbai-orders/export-excel', { responseType: 'blob' });
      const day = new Date().toISOString().slice(0, 10);
      downloadBlob(
        response.data,
        `הזמנות_גבאים_${day}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      );
    } catch (e) {
      console.error('שגיאה בייצוא הזמנות:', e);
      setError('שגיאה בייצוא הזמנות');
    } finally {
      setExporting('');
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    setError('');
    try {
      const path = format === 'pdf'
        ? '/gabbai-orders/export-material-pdf'
        : '/gabbai-orders/export-material-excel';
      const response = await api.get(path, { responseType: 'blob' });
      const day = new Date().toISOString().slice(0, 10);
      if (format === 'pdf') {
        downloadBlob(response.data, `סיכום_חומר_הזמנות_${day}.pdf`, 'application/pdf');
      } else {
        downloadBlob(
          response.data,
          `סיכום_חומר_הזמנות_${day}.xlsx`,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );
      }
    } catch (e) {
      console.error('שגיאה בייצוא סיכום חומר:', e);
      setError('שגיאה בייצוא סיכום חומר');
    } finally {
      setExporting('');
    }
  };

  return (
    <Box sx={{ p: inNextDesign ? 0 : 2 }}>
      <AdminPageHeader
        title="הזמנות שאלונים — גבאים"
        sub="סה״כ כמויות לפי מבחן ועיר מיקום. כל כולל (גבאי) מעדכן רק את השורה שלו; הסכום מצטבר מכל הכוללים."
        menus={[{
          key: 'export',
          label: 'ייצוא',
          icon: 'download',
          items: [
            { key: 'orders', label: exporting === 'orders' ? 'מייצא…' : 'ייצוא הזמנות Excel', icon: 'download', onClick: handleExportOrders, disabled: !!exporting || loading || !data.length },
            { key: 'excel', label: exporting === 'excel' ? 'מייצא…' : 'סיכום חומר Excel', icon: 'download', onClick: () => handleExport('excel'), disabled: !!exporting || loading || !data.length },
            { key: 'pdf', label: exporting === 'pdf' ? 'מייצא…' : 'סיכום חומר PDF', icon: 'file', onClick: () => handleExport('pdf'), disabled: !!exporting || loading || !data.length },
          ],
        }]}
        legacy={(
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Inventory2OutlinedIcon color="primary" />
                <Typography variant="h5">הזמנות שאלונים — גבאים</Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={handleExportOrders}
                  disabled={!!exporting || loading || !data.length}
                >
                  {exporting === 'orders' ? 'מייצא…' : 'ייצוא הזמנות Excel'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={() => handleExport('excel')}
                  disabled={!!exporting || loading || !data.length}
                >
                  {exporting === 'excel' ? 'מייצא…' : 'סיכום חומר Excel'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  onClick={() => handleExport('pdf')}
                  disabled={!!exporting || loading || !data.length}
                >
                  {exporting === 'pdf' ? 'מייצא…' : 'סיכום חומר PDF'}
                </Button>
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              סה״כ כמויות לפי מבחן ועיר מיקום. כל כולל (גבאי) מעדכן רק את השורה שלו; הסכום מצטבר מכל הכוללים.
              ייצוא הזמנות — קוד מבחן, שם וכמות מצטברת. ייצוא סיכום חומר — ארבעת חלקי השו״ע וטווחי הסימנים.
            </Typography>
          </>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress size={28} />}

      {!loading && !data.length && (
        <Alert severity="info">אין הזמנות גבאים עדיין</Alert>
      )}

      {!loading && data.length > 0 && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell>קוד מבחן</TableCell>
                <TableCell>מבחן</TableCell>
                <TableCell>עיר מיקום</TableCell>
                <TableCell align="right">סה״כ כמות</TableCell>
                <TableCell align="right">גבאים</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => {
                const key = `${row.examId}::${row.location}`;
                const open = !!expanded[key];
                return (
                  <React.Fragment key={key}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggle(key)}>
                          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{row.examCode || '—'}</TableCell>
                      <TableCell>{row.examName || '—'}</TableCell>
                      <TableCell>{row.location || '—'}</TableCell>
                      <TableCell align="right">
                        <Chip label={row.totalQuantity} color="primary" size="small" />
                      </TableCell>
                      <TableCell align="right">{row.gabbaim?.length || 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1, px: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>כולל</TableCell>
                                  <TableCell>גבאי</TableCell>
                                  <TableCell>טלפון</TableCell>
                                  <TableCell align="right">כמות</TableCell>
                                  <TableCell>עודכן</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(row.gabbaim || []).map((g) => (
                                  <TableRow key={g.orderId}>
                                    <TableCell>
                                      {g.kolelName
                                        ? `${g.kolelName}${g.kolelCode != null && g.kolelCode !== '' ? ` (${g.kolelCode})` : ''}`
                                        : '—'}
                                    </TableCell>
                                    <TableCell>{g.gabbaiName || '—'}</TableCell>
                                    <TableCell>{g.phone || '—'}</TableCell>
                                    <TableCell align="right">{g.quantity}</TableCell>
                                    <TableCell>
                                      {g.updatedAt
                                        ? formatHebrewDateTime(g.updatedAt)
                                        : '—'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
